/**
 * Alerta sonoro discreto "ding" para KDS (Fase 12).
 * Usa Web Audio API — sem depender de asset externo.
 * Toca apenas se já houve interação do usuário com a página (política de autoplay).
 */

let audioCtx = null;
let unlocked = false;

function getContext() {
  if (audioCtx) return audioCtx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  audioCtx = new AC();
  return audioCtx;
}

/**
 * "Desbloqueia" o AudioContext após a primeira interação.
 * Alguns browsers (Chrome/Safari) bloqueiam audioCtx até user gesture.
 * Deve ser chamado dentro de um handler de clique/tecla.
 */
export function unlockAudio() {
  if (unlocked) return;
  const ctx = getContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});
  unlocked = true;
}

let autoUnlockInstalled = false;

/**
 * v7.71 — Instala unlock automático no PRIMEIRO gesto do usuário na página
 * (toque/clique/tecla). Sem isso, o ding de "novo pedido" ficava mudo até o
 * operador clicar no toggle de som — política de autoplay dos browsers.
 */
export function installAutoUnlock() {
  if (autoUnlockInstalled) return;
  autoUnlockInstalled = true;
  const handler = () => {
    unlockAudio();
    document.removeEventListener('pointerdown', handler);
    document.removeEventListener('keydown', handler);
  };
  document.addEventListener('pointerdown', handler);
  document.addEventListener('keydown', handler);
}

/**
 * Toca um "ding" discreto (duas notas, ~280ms).
 * Silencioso e gentil — pensado para o ambiente da cozinha.
 */
export function playDing({ volume = 0.25 } = {}) {
  const ctx = getContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
  const now = ctx.currentTime;

  const makeTone = (freq, startAt, duration = 0.22) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, startAt);
    gain.gain.setValueAtTime(0, startAt);
    gain.gain.linearRampToValueAtTime(volume, startAt + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(startAt);
    osc.stop(startAt + duration + 0.02);
  };

  // Acorde de 2 notas (Sol 5 + Dó 6) — timbre "ding"
  makeTone(784, now);
  makeTone(1046, now + 0.09);
}

/**
 * v7.81 — Alerta de NOVO PEDIDO: bipe alto, claro e repetido (3 ciclos de
 * duas notas ascendentes) — pensado pra cozinha barulhenta. O playDing
 * discreto continua sendo usado para mudanças de status.
 */
export function playNewOrderAlert({ volume = 0.6 } = {}) {
  const ctx = getContext();
  if (!ctx) return;
  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }
  const now = ctx.currentTime;
  const tone = (freq, startAt, duration = 0.16) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'square';
    osc.frequency.setValueAtTime(freq, startAt);
    gain.gain.setValueAtTime(0, startAt);
    gain.gain.linearRampToValueAtTime(volume, startAt + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(startAt);
    osc.stop(startAt + duration + 0.02);
  };
  // 3 ciclos "bi-bip" ascendentes espaçados de 0.35s
  for (let i = 0; i < 3; i++) {
    const t = now + i * 0.35;
    tone(880, t);
    tone(1318, t + 0.14);
  }
}
